import { FC } from 'react';
import BasicModal from '../common/BasicModal';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, TextField } from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';

const adRejectDialogSchema = z.object({
	reason: z.string().min(5, { message: 'this_field_is_required' }),
	explanation: z.string().min(5, { message: 'this_field_is_required' }),
});

type FormValues = z.infer<typeof adRejectDialogSchema>;

type IPaymentModal = {
	open: boolean;
	submitting: boolean;
	onClose: () => void;
	onSubmit: (title: string, description: string) => Promise<void>;
};

const AdRejectDialog: FC<IPaymentModal> = ({ onSubmit, open, onClose, submitting }) => {
	const { t } = useTranslation();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		resolver: zodResolver(adRejectDialogSchema),
	});

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleReject = async (data: FormValues) => {
		onSubmit(data.reason, data.explanation).finally(() => {
			reset();
		});
	};

	return (
		<BasicModal className="h-auto max-w-[690px]" open={open} onClose={onClose} titleKey="ad_reject_reason" showBackIcon={false}>
			<form className="flex w-full flex-col justify-between gap-x-4 px-4 pt-8 sm:px-8" onSubmit={handleSubmit(handleReject)}>
				<FormControl fullWidth>
					<TextField id="reason" label={t('ad_reject_reason')} variant="outlined" error={!!errors.reason} helperText={errors.reason ? t(errors?.reason?.message!) : ''} {...control.register('reason')} />
					<TextField id="explanation" className="mt-6 w-full" label={t('explanation')} multiline minRows={5} error={!!errors.explanation} helperText={errors.explanation ? t(errors?.explanation?.message!) : ''} {...control.register('explanation')} />
				</FormControl>
				<div className="mt-5 flex flex-row-reverse gap-3">
					<LoadingButton loading={submitting} variant="contained" className="my-2 w-36 bg-primary-main text-white" type="submit">
						{submitting ? '' : t('register')}
					</LoadingButton>
					<Button variant="outlined" className="my-2 w-28 border-gray-400 text-gray-400" onClick={handleClose}>
						{t('cancel')}
					</Button>
				</div>
			</form>
		</BasicModal>
	);
};

export default AdRejectDialog;
